import { useEffect, useState } from 'react';

import { useBankDossier } from '../../../api/workOrders/bankDossier';
import { WorkOrderData } from '../../../api/workOrders/types';
import {
  Constants,
  DossierSectionId,
  dossierSectionOrder,
} from '../../../constants/bankDossier';
import { AccordionTitle } from '../../Sections/AccordionTitle';
import { WorkOrderProgressBar } from '../../Sections/ProgressBar';
import { SectionBox } from '../../UI/Box';
import { dossierSectionDomId, OPEN_DOSSIER_SECTION_EVENT } from '../events';
import { sectionIcons } from './sectionIcons';
import { ChargesSection } from './sections/ChargesSection';
import { ContactsSection } from './sections/ContactsSection';
import { FilesSection } from './sections/FilesSection';
import { FinancingSection } from './sections/FinancingSection';
import { PartiesSection } from './sections/PartiesSection';
import { RelatedRequestsSection } from './sections/RelatedRequestsSection';
import { TimelineSection } from './sections/TimelineSection';
import { SectionStack } from './styles';

type OpenState = Record<DossierSectionId, boolean>;

const allClosed = (): OpenState =>
  Object.fromEntries(dossierSectionOrder.map((id) => [id, false])) as OpenState;

const allSet = (value: boolean): OpenState =>
  Object.fromEntries(dossierSectionOrder.map((id) => [id, value])) as OpenState;

interface DossierSectionsProps {
  propertyData: WorkOrderData | undefined;
  /** Mirrors the page's "expandir todos" toggle. */
  expandAll?: boolean;
  /** Wrap each section with the page progress bar (false on Arquivados). */
  withProgressBar?: boolean;
  /** Whether the last dossier section closes the progress line. */
  isLast?: boolean;
}

/**
 * The seven bank dossier sections, rendered as accordions in the same visual
 * pattern as the other work order sections. Open state is local so pages do
 * not need more slots in `useAccordion`; `expandAll` keeps them in sync with
 * the page toggle.
 */
export function DossierSections({
  propertyData,
  expandAll,
  withProgressBar = true,
  isLast = true,
}: DossierSectionsProps): JSX.Element | null {
  const dossier = useBankDossier(propertyData);
  const [open, setOpen] = useState<OpenState>(allClosed);

  useEffect(() => {
    if (expandAll === undefined) return;
    setOpen(allSet(expandAll));
  }, [expandAll]);

  useEffect(() => {
    const handler = (event: Event): void => {
      const id = (event as CustomEvent<DossierSectionId>).detail;
      if (!dossierSectionOrder.includes(id)) return;
      setOpen((current) => ({ ...current, [id]: true }));
      window.setTimeout(() => {
        document
          .getElementById(dossierSectionDomId(id))
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    };
    window.addEventListener(OPEN_DOSSIER_SECTION_EVENT, handler);
    return () =>
      window.removeEventListener(OPEN_DOSSIER_SECTION_EVENT, handler);
  }, []);

  if (!propertyData || !dossier) return null;

  const renderBody = (id: DossierSectionId): JSX.Element => {
    switch (id) {
      case 'financing':
        return <FinancingSection dossier={dossier} workOrder={propertyData} />;
      case 'timeline':
        return <TimelineSection dossier={dossier} />;
      case 'parties':
        return <PartiesSection dossier={dossier} workOrder={propertyData} />;
      case 'contacts':
        return <ContactsSection dossier={dossier} />;
      case 'charges':
        return <ChargesSection dossier={dossier} />;
      case 'files':
        return <FilesSection dossier={dossier} workOrder={propertyData} />;
      case 'related':
      default:
        return (
          <RelatedRequestsSection dossier={dossier} workOrder={propertyData} />
        );
    }
  };

  return (
    <>
      {dossierSectionOrder.map((id, index) => {
        const section = (
          <SectionBox
            id={dossierSectionDomId(id)}
            key={id}
            sx={
              withProgressBar
                ? undefined
                : (theme) => ({
                    margin: `0 0 ${theme.palette.tokens.space[5]}px`,
                  })
            }
          >
            <AccordionTitle
              title={Constants.sections[id]}
              icon={sectionIcons[id]}
              openAccordion={open[id]}
              setOpenAccordion={(value) =>
                setOpen((current) => ({ ...current, [id]: value }))
              }
            />
            {open[id] && <SectionStack>{renderBody(id)}</SectionStack>}
          </SectionBox>
        );

        if (!withProgressBar) return section;

        return (
          <WorkOrderProgressBar
            key={id}
            complete
            lastAccordion={isLast && index === dossierSectionOrder.length - 1}
          >
            {section}
          </WorkOrderProgressBar>
        );
      })}
    </>
  );
}
